import { formatBRL } from '@/lib/charts/formatters';
import { token } from '@/lib/theme/tokens';

/** Cap tooltip rows so a month with many active installments can't overload on mobile. */
export const MAX_TOOLTIP_ROWS = 6;

export interface RunoffParam {
  seriesName?: string;
  value?: unknown;
  color?: string;
  axisValueLabel?: string;
  name?: string;
}

function rowHtml(color: string, name: string, value: number): string {
  return `<div style="display:flex;align-items:center;justify-content:space-between;gap:14px;padding:2px 0;">
      <span style="display:flex;align-items:center;gap:6px;min-width:0;color:#cbd5e1;font-size:12px;"><span style="width:8px;height:8px;border-radius:50%;background:${color};flex:none;"></span><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:150px;">${name}</span></span>
      <span style="font-variant-numeric:tabular-nums;color:#f8fafc;font-size:12px;flex:none;">${formatBRL(value)}</span>
    </div>`;
}

/**
 * Builds the "Liberação de parcelas" tooltip: the hovered month split by installment. Sorts by value,
 * shows the top {@link MAX_TOOLTIP_ROWS} parcelas, folds the rest into "+N outros", and prints the
 * month total — so a month with many parcelas stays compact on a phone screen.
 */
export function buildRunoffTooltipHtml(raw: unknown): string {
  const params = (Array.isArray(raw) ? raw : [raw]) as RunoffParam[];
  const live = params
    .filter((p) => typeof p.value === 'number' && (p.value as number) > 0)
    .sort((a, b) => (b.value as number) - (a.value as number));
  if (live.length === 0) return '';

  const title = params[0]?.axisValueLabel ?? params[0]?.name ?? '';
  const total = live.reduce((sum, p) => sum + (p.value as number), 0);

  const shown = live.slice(0, MAX_TOOLTIP_ROWS);
  const rest = live.slice(MAX_TOOLTIP_ROWS);
  let rows = shown.map((p) => rowHtml(p.color ?? token('--warning', '#d97706'), p.seriesName ?? '', p.value as number)).join('');
  if (rest.length > 0) {
    const restTotal = rest.reduce((sum, p) => sum + (p.value as number), 0);
    rows += rowHtml(token('--text-tertiary', '#a8a29e'), `+${rest.length} ${rest.length === 1 ? 'outro' : 'outros'}`, restTotal);
  }

  const totalColor = token('--warning', '#fbbf24');
  return `<div style="max-width:240px;"><div style="font-size:11px;font-weight:600;color:#e2e8f0;margin-bottom:6px;">${title} · ${live.length} ${live.length === 1 ? 'parcela' : 'parcelas'}</div>${rows}<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(148,163,184,0.2);display:flex;justify-content:space-between;gap:14px;"><span style="color:#94a3b8;font-size:12px;">Comprometido</span><span style="color:${totalColor};font-variant-numeric:tabular-nums;font-size:12px;">${formatBRL(total)}</span></div></div>`;
}
